import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function DeliveredAreaForm({
  deliveredAreaData,
  setDeliveredAreaData,
  onSave,
  apiBase = `${API_BASE_URL}/about`,
}) {
  const [formData, setFormData] = useState([]);
  const [totalArea, setTotalArea] = useState(0);

  // ✅ Pre-fill data if available
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiBase}/delivered-area`);
        if (response.ok) {
          const data = await response.json();
          const areaData = Array.isArray(data) ? data : [];
          setFormData(areaData);
          if (setDeliveredAreaData) setDeliveredAreaData(areaData);
        }
      } catch (err) {
        console.error("Error fetching delivered area data:", err);
        setFormData([]);
      }
    };

    fetchData();
  }, [apiBase]);

  // ✅ Auto-calculate total area whenever formData changes
  useEffect(() => {
    const total = formData.reduce((acc, item) => {
      const areaValue = parseInt(item.area.replace(/,/g, "")) || 0;
      return acc + areaValue;
    }, 0);
    setTotalArea(total);
  }, [formData]);

  // ✅ Handle input changes
  const handleChange = (index, field, value) => {
    const updated = [...formData];
    updated[index][field] = value;
    setFormData(updated);
    if (setDeliveredAreaData) setDeliveredAreaData(updated);
  };

  // ✅ Add new row
  const handleAddRow = () => {
  const newRow = { project: "", area: "" };
  setFormData((prev) => {
    const updated = [newRow, ...prev];
    if (setDeliveredAreaData) setDeliveredAreaData(updated);
    return updated;
  });
};


  // ✅ Delete row
  const handleDeleteRow = (index) => {
    if (window.confirm("Are you sure you want to remove this project?")) {
      const updated = formData.filter((_, i) => i !== index);
      setFormData(updated);
      if (setDeliveredAreaData) setDeliveredAreaData(updated);
    }
  };

  // ✅ Save
  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Session expired. Please log in again.");
        return;
      }

      // Validate data
      const hasEmptyFields = formData.some(
        (item) => !item.project.trim() || !item.area.trim()
      );

      if (hasEmptyFields) {
        alert("Please fill in all project names and areas before saving.");
        return;
      }

      const res = await fetch(`${apiBase}/delivered-area`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to save delivered area data.");

      const result = await res.json();
      alert(result.message || "Delivered area data saved successfully!");
      onSave?.(formData);
    } catch (error) {
      console.error("Error saving delivered area data:", error);
      alert("Failed to save. Check console for details.");
    }
  };

  // ✅ Format number with commas
  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Delivered Area by Project
        </h2>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
        >
          <Save size={18} /> Save
        </button>
      </div>

      {/* Data Rows */}
      <div className="space-y-6">
        {formData.length > 0 ? (
          formData.map((item, index) => (
            <div
              key={index}
              className="flex flex-col sm:flex-row justify-between items-center gap-4 border-b border-gray-200 pb-4"
            >
              <input
                type="text"
                placeholder="Project Name"
                value={item.project || ""}
                onChange={(e) => handleChange(index, "project", e.target.value)}
                className="w-full sm:w-2/3 border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
              <div className="flex items-center gap-3 w-full sm:w-1/3">
                <input
                  type="text"
                  placeholder="Area (sq ft)"
                  value={item.area || ""}
                  onChange={(e) => handleChange(index, "area", e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
                />
                <button
                  onClick={() => handleDeleteRow(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500 text-base">
              No projects added yet. Click "Add Project" to get started.
            </p>
          </div>
        )}

        {/* Add New Row */}
        <button
          onClick={handleAddRow}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg mt-4"
        >
          <Plus size={18} /> Add Project
        </button>
      </div>

      {/* Total Section */}
      <div className="flex justify-between items-center py-4 bg-gradient-to-r from-[#1A81BA] to-[#5AA650] text-white px-6 rounded-lg mt-8">
        <span className="text-xl font-bold">Total Delivered</span>
        <span className="text-xl font-bold">
          {formatNumber(totalArea)} sq ft
        </span>
      </div>
    </section>
  );
}